import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { darken, lighten } from '@mui/material/styles';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import HelpCircleWithContainer from 'src/components/HelpCircle/HelpCircleWithContainer';

export const PERSONALIZED_LINK_LABEL = 'Each contact gets their own link';
export const PERSONALIZED_LINK_WARNING = "Don't use for links that get forwarded, like a host sharing with guests.";

/* *************************************************************************
 * PersonalizedLinkCheckbox
 * The "Each contact gets their own link" row shared by the Insert Smart Form
 * URL / Connect Page picker and the Chat Insert Smart Form modal.
 *
 * CON-1852: a personalized link that gets forwarded (host -> guests) lets
 * every guest submission overwrite the host's contact. While the warning is
 * on, the row takes the standard MUI warning Alert colors and icon (computed
 * the same way Alert severity="warning" does, so no new palette) plus a
 * second line saying when not to use it. Presentation only — the checked
 * state and token behavior belong to the caller.
 *************************************************************************** */
function PersonalizedLinkCheckbox({
  checked,
  onChange,
  disabled = false,
  helpText = null,
  showWarning = false,
}) {
  return (
    <Box
      sx={showWarning ? {
        mt: 1,
        py: 0.5,
        pr: 1,
        borderRadius: 1,
        color: (theme) => darken(theme.palette.warning.light, 0.6),
        backgroundColor: (theme) => lighten(theme.palette.warning.light, 0.9),
      } : { mt: 1 }}
    >
      <FormControlLabel
        control={(
          <Checkbox
            checked={checked}
            onChange={onChange}
            size="small"
            disabled={disabled}
          />
        )}
        label={(
          <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0 }}>
            {showWarning && (
              <ReportProblemOutlinedIcon
                sx={{
                  flexShrink: 0,
                  fontSize: 18,
                  mt: '1px',
                  mr: 0.5,
                  color: 'warning.main',
                }}
              />
            )}
            <Box sx={{ minWidth: 0, flexShrink: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography
                  variant="caption"
                  color={showWarning ? 'inherit' : 'textSecondary'}
                  sx={showWarning ? { fontWeight: 500 } : undefined}
                >
                  {PERSONALIZED_LINK_LABEL}
                </Typography>
                {helpText && (
                  <HelpCircleWithContainer
                    positionX="afterText"
                    positionY="center"
                    isShowInfoV2
                    showInfo={helpText}
                  />
                )}
              </Box>
              {showWarning && (
                <Typography
                  variant="caption"
                  component="div"
                  color="inherit"
                  sx={{ lineHeight: 1.35, overflowWrap: 'anywhere' }}
                >
                  {PERSONALIZED_LINK_WARNING}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        sx={{
          ml: '-11px',
          mr: 0,
          textAlign: 'left',
          alignItems: showWarning ? 'flex-start' : 'center',
          // The box is disabled until a form is chosen; keep the warning
          // readable rather than greyed out while it waits.
          ...(showWarning ? {
            '& .MuiCheckbox-root': { pt: '3px' },
            '& .MuiFormControlLabel-label.Mui-disabled': { color: 'inherit' },
          } : {}),
        }}
      />
    </Box>
  );
}

PersonalizedLinkCheckbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  helpText: PropTypes.node,
  showWarning: PropTypes.bool,
};

export default PersonalizedLinkCheckbox;
